/**
 * weatherLayerConfig.ts — single source of truth for all weather layer parameters.
 *
 * Each layer defines:
 *   prefix          R2 key prefix for COG manifest filtering
 *   label, unit     UI display
 *   dataRange       [min, max] in original physical units (°F, dBZ, m/s, etc.)
 *   decode          how to extract the raw scaled value from the texture's RGB channels
 *                   - "R"    single channel, value = R * (max-min) + min
 *                   - "RG"   two channels, vector data (used for wind u,v encoded in R,G)
 *                   - "RGB"  packed 24-bit value (high precision scalar)
 *   palette         sparse color stops in normalized [0..1] space
 *   legendLabels    UI labels for the legend bar
 *   smooth          enable in-shader 3x3 Gaussian blur on the decoded value
 *   precipTypePalettes  for MRMS-style precip-type encoded layers, per-type
 *                   palettes that get baked into a flat 256-entry LUT with
 *                   transition zones at the category boundaries
 *
 * The shader reads the raw scaled texture value x in [0..1] (after bilinear sampling),
 * optionally blurs it, interpolates between frames, and samples the palette LUT.
 *
 * For simple linear ranges (the common case), the polynomial is just y = (max-min)*x + min.
 */

export type Rgba = [number, number, number, number];
export type PaletteStop = [number, Rgba]; // [position 0-1, [r,g,b,a] 0-255]

export interface LayerConfig {
  label: string;
  prefix: string;
  unit: string;
  dataRange: [number, number];
  decode: "R" | "RG" | "RGB";
  // Which channel of the packed RGB texture holds this layer's data.
  // Only meaningful for `decode: "R"`. The COG worker packs band 1→R, band 2→G,
  // band 3→B, so most COGs that encode multiple physical variables in one file
  // (e.g. our temperature/visibility/satellite triplets) need this set per
  // sibling layer. Defaults to "R" if omitted (preserves single-band behavior).
  band?: "R" | "G" | "B";
  // When false, the SMOOTH==0 shader path will NOT discard pixels whose
  // decoded value is exactly 0. Default is true (preserves the historical
  // behaviour where most layers use byte 0 as a no-data sentinel and need
  // those pixels thrown away). Layers where byte 0 is meaningful data —
  // cloud cover at full coverage, visibility at zero metres, anything
  // that legitimately reaches the bottom of its palette — must set this
  // to false or risk holes through valid imagery.
  discardZero?: boolean;
  // When true, bytes follow the updated GFS pipeline quantization:
  //   byte = 1 + 254 * (v - vmin) / (vmax - vmin), byte 0 reserved for NoData.
  // The shader then dequantizes with (byte - 1) / 254 instead of byte / 255.
  // Layers from other pipelines (MRMS, satellite) keep the legacy byte/255
  // mapping and leave this unset.
  quantized254?: boolean;
  palette: PaletteStop[];
  legendLabels: string[];
  // Downsampling factor applied at decode time (default 1, no downsample).
  // Set to 2 for very large rasters (e.g. MRMS radar at 6553×4279) to cut
  // memory 4× with minimal visual quality loss at typical zoom levels.
  downsample?: number;
  // Maximum number of frames to keep in cache for this layer (default 12).
  // Large rasters benefit from smaller caches; compact layers can cache more.
  maxCacheSize?: number;
  // Spatial smoothing (3x3 Gaussian in-shader).
  smooth?: boolean;
  // Motion-warped time interpolation using optical flow.
  // When true, the renderer computes a motion vector field between every
  // consecutive frame pair and warps pixels along those vectors during
  // playback — producing real apparent motion instead of cross-fade.
  // Only makes sense for data where moving cells are the primary feature
  // (radar). Costs ~100-200ms per frame pair to compute, cached after that.
  motionWarp?: boolean;
  // Three-pass separable-Gaussian smoothing rendered through offscreen
  // framebuffers. Replaces the single-pass 29-tap kernel (SMOOTH==2) with a
  // pass-1 categorize → pass-2 horizontal blur → pass-3 vertical blur+colorize
  // pipeline. The first pass writes per-category data into an FBO using a
  // continuous encoding that's safe under hardware bilinear filtering; the
  // blur passes then use real bilinear sampling, which eliminates the
  // axis-aligned "kernel-signature" contour boxes that the single-pass
  // kernel produces at high zoom on stair-stepped source data.
  // Only meaningful when precipTypePalettes is also set. Default off so the
  // existing single-pass path remains the fallback.
  multiPassSmooth?: boolean;
  // A/B test flag for the simple-bilinear precip-type path (SMOOTH==5 in the
  // shader). When true (alongside precipTypePalettes), bypasses the
  // category-aware kernel pipeline entirely and renders via sentinel-safe
  // bilinear + temporal blend + LUT lookup. This is the minimal "WSV3-style"
  // path, intended to evaluate whether the complex SMOOTH==2 machinery is
  // earning its keep. Overrides multiPassSmooth and motionWarp when set, since
  // the whole point is to be simple.
  // To turn on for testing: set simplePrecipType: true on the MRMS layer in
  // WEATHER_LAYERS, reload the page. Compare visually against the same layer
  // with simplePrecipType: false (the existing default).
  simplePrecipType?: boolean;
  // Triangle-kernel half-width for multi-frame temporal smoothing. Pass 1 of
  // the multipass pipeline samples four temporal neighbors (frames N-1, N,
  // N+1, N+2) and blends them with weights from a triangle window of this
  // half-width. 1.0 = standard cross-fade between the central pair only.
  // Larger values pull in N-1 and N+2 progressively, producing a temporal
  // motion blur that masks the visible "snap" between widely-spaced frames
  // (where consecutive frames show storms in different positions). Typical
  // values: 1.5 for mild blur, 2.0 for aggressive. Only used when
  // multiPassSmooth is also set.
  temporalBlurWidth?: number;
  // Playback seconds per keyframe (default 1.5). Larger values slow animation,
  // which masks inter-frame motion for fast-changing data like MRMS radar.
  secondsPerFrame?: number;
  // Precip-type encoded layers (MRMS): byte value encodes BOTH category and
  // intensity. Supply three sub-palettes; the layer builds a single 256-entry
  // LUT with transition zones so the whole thing acts like a continuous layer.
  precipTypePalettes?: {
    rain: PaletteStop[];
    sleet: PaletteStop[];
    snow: PaletteStop[];
  };
  // When true, this layer is not shown in the sidebar list. Used for sub-layers
  // that are only activated indirectly through a composite entry.
  hidden?: boolean;
  // Composite layers render multiple sub-layers simultaneously (e.g. a single
  // "Radar" entry that shows MRMS over CONUS and EUMETNET over Europe at the
  // same time). Each sub-key must refer to another entry in WEATHER_LAYERS.
  // The composite entry itself does NOT have a raster prefix / data of its
  // own — all rendering state lives in the sub-layers.
  composite?: string[];
  // Optional R2 prefix for nowcast (forecast) frames. When set AND the
  // runtime flag NOWCAST_ENABLED is true, frames under this prefix are
  // appended to the realtime keyframe list so the scrubber extends into
  // the future. The nowcast files are assumed to use the same byte
  // encoding and timestamp filename format as the realtime files.
  nowcastPrefix?: string;
}

// Convert a 5-tuple [value, r, g, b, a] ramp (as used in your old code) into
// normalized palette stops.
function normalizeRamp(ramp: number[][]): PaletteStop[] {
  const min = ramp[0][0];
  const max = ramp[ramp.length - 1][0];
  const range = max - min || 1;
  return ramp.map(([v, r, g, b, a]) => [
    (v - min) / range,
    [r, g, b, a] as Rgba,
  ]);
}

// Shared palette used by the temperature & apparent-temperature bands.
// Derived from the WSV3 temps2025.wspal palette, with the value→position
// mapping renormalized to the K-band data range (220K–320K = -63.67°F–116.33°F).
// Stops outside that range (the palette's -100..-60°F and 120..140°F tails)
// are interpolated to the boundary positions so no source-palette colors are
// thrown away off-screen — they're just compressed to the legend endpoints.
const TEMPERATURE_PALETTE: PaletteStop[] = [
  [0.0000, [255, 235, 255, 255]], [0.0204, [255, 236, 255, 255]],
  [0.0759, [255, 255, 255, 255]], [0.1315, [241, 254, 255, 255]],
  [0.1871, [225, 249, 255, 255]], [0.2426, [164, 243, 255, 255]],
  [0.2704, [120, 197, 255, 255]], [0.2982, [140, 160, 255, 255]],
  [0.3259, [167, 145, 255, 255]], [0.3537, [176, 116, 255, 255]],
  [0.3815, [167, 65, 255, 255]],  [0.4093, [216, 63, 255, 255]],
  [0.4371, [170, 16, 242, 255]],  [0.4648, [133, 32, 224, 255]],
  [0.4926, [87, 62, 219, 255]],   [0.5204, [84, 108, 245, 255]],
  [0.5482, [104, 149, 255, 255]], [0.5759, [134, 161, 255, 255]],
  [0.6037, [174, 218, 255, 255]], [0.6315, [173, 255, 221, 255]],
  [0.6593, [132, 224, 160, 255]], [0.6871, [71, 204, 85, 255]],
  [0.7148, [97, 209, 22, 255]],   [0.7426, [210, 237, 20, 255]],
  [0.7593, [245, 241, 8, 255]],   [0.7704, [245, 208, 4, 255]],
  [0.7982, [255, 116, 30, 255]],  [0.8259, [255, 33, 32, 255]],
  // 85–110°F: replaced the original pink/magenta tail with a red→dark red→
  // white ramp. Dark red emphasizes the dangerously hot range (90–100°F)
  // rather than fading into a soft pink that visually de-emphasizes it.
  [0.8537, [200, 15,  25,  255]], // 85°F  dark red
  [0.8815, [150, 10,  20,  255]], // 90°F  deeper dark red
  [0.9093, [120, 8,   18,  255]], // 95°F  darkest red
  [0.9371, [180, 100, 100, 255]], // 100°F warm pale (transition to white)
  [0.9648, [220, 180, 180, 255]], // 105°F lighter warm pale
  [0.9926, [248, 240, 240, 255]], // 115°F near white
  [1.0000, [255, 255, 255, 255]],
];

// Dewpoint-specific palette (WSV3 dewpoint2025 / "M_2MD"). Renormalized to
// the same -63.67..116.33°F range as TEMPERATURE_PALETTE. Conceptually the
// same encoding as the temp band — different colors because dewpoint is
// conventionally rendered with the green/brown moisture-emphasizing ramp
// rather than the rainbow temperature ramp.
const DEWPOINT_PALETTE: PaletteStop[] = [
  [0.0000, [248, 248, 248, 255]], [0.1593, [242, 242, 242, 255]],
  [0.2982, [120, 210, 230, 255]], [0.3537, [156, 173, 243, 255]],
  [0.3815, [177, 187, 239, 255]], [0.4371, [238, 220, 229, 255]],
  [0.4648, [239, 227, 220, 255]], [0.5759, [207, 235, 148, 255]],
  [0.6037, [172, 212, 102, 255]], [0.6593, [80, 204, 43, 255]],
  [0.6871, [0, 200, 27, 255]],    [0.7148, [0, 175, 23, 255]],
  [0.7426, [0, 150, 15, 255]],    [0.8537, [0, 70, 10, 255]],
  [0.9648, [0, 60, 5, 255]],      [1.0000, [0, 60, 5, 255]],
];

// Humidity-specific blue ramp. Same stop positions as DEWPOINT_PALETTE so
// values map to comparable visual weight, but rendered as a continuous blue
// gradient: pale near-white at 0% (dry) → light blue → mid blue → deep navy
// at 100% (saturated). Independent from DEWPOINT_PALETTE because the user
// wanted RH visually distinct rather than identical to dewpoint.
const HUMIDITY_PALETTE: PaletteStop[] = [
  [0.0000, [248, 250, 255, 255]], [0.1593, [225, 235, 250, 255]],
  [0.2982, [180, 210, 240, 255]], [0.3537, [150, 195, 235, 255]],
  [0.3815, [120, 180, 230, 255]], [0.4371, [90,  165, 225, 255]],
  [0.4648, [70,  150, 220, 255]], [0.5759, [50,  130, 215, 255]],
  [0.6037, [35,  110, 205, 255]], [0.6593, [25,  90,  195, 255]],
  [0.6871, [20,  75,  180, 255]], [0.7148, [15,  60,  165, 255]],
  [0.7426, [10,  50,  150, 255]], [0.8537, [8,   35,  120, 255]],
  [0.9648, [5,   20,  90,  255]], [1.0000, [3,   15,  70,  255]],
];

// GMGSI infrared satellite palette (WSV3 "Ifrared Sat 2026"). The byte 0..255
// represents brightness temperature normalized from the 150..280 K design
// range. Lower bytes = colder cloud tops (more white/visible); the highest
// bytes fade to transparent so warm-surface "clear sky" pixels don't paint
// the basemap. Currently unused — longwave/shortwave reverted to plain
// grayscale per request — but kept here for reference / future re-use.
const SATELLITE_IR_PALETTE: PaletteStop[] = [
  [0.0000, [169, 169, 169, 255]], [0.1538, [58, 58, 58, 255]],
  [0.2308, [255, 255, 255, 255]], [0.3077, [0, 0, 0, 255]],
  [0.3846, [76, 76, 76, 255]],    [0.4615, [223, 223, 223, 255]],
  [0.5385, [149, 149, 149, 255]], [0.6154, [15, 15, 15, 255]],
  [0.7308, [170, 170, 170, 255]], [1.0000, [255, 255, 255, 0]],
];

// Simple linear grayscale used by the longwave & shortwave satellite bands.
// Matches the original project's `satellite` layer palette: dark byte 0,
// bright byte 255, mild semi-transparency throughout so the basemap can
// breathe through the imagery. Water-vapor uses its own colorized ramp.
const SATELLITE_GRAYSCALE: PaletteStop[] = [
  [0.00, [0, 0, 0, 200]],       [0.25, [64, 64, 64, 200]],
  [0.50, [128, 128, 128, 200]], [0.75, [192, 192, 192, 200]],
  [1.00, [255, 255, 255, 200]],
];

export const WEATHER_LAYERS: Record<string, LayerConfig> = {
  // ─── Temperature triplet ─────────────────────────────────────────────────
  // Source COG (prefix "nwp/gfs/temperature") packs 3 physical
  // variables into the RGB bands per the updated GFS RGB COG spec. Worker maps
  // band 1→R, band 2→G, band 3→B. Each entry below selects one channel via
  // `band`. NOTE the R/G order changed vs. the legacy temp_layer files:
  //   Band 1 (R): Temperature 2m    (220K..320K → -63.67°F..116.33°F)
  //   Band 2 (G): Dewpoint 2m       (220K..320K → -63.67°F..116.33°F)
  //   Band 3 (B): Apparent temperature (220K..320K → -63.67°F..116.33°F)
  // Bytes use the 1..255 quantization (0 = NoData) → quantized254.
  // dataRange is given in °F so the legend renders in the units the user expects.
  // The palette below is the same continuous ramp used for the original
  // temperature layer; because the K range maps to a sub-range of the old
  // -100..140°F display, the visual coloring will sit in the middle portion
  // of that ramp (no white extreme-cold / pink extreme-heat tails).
  dewpoint: {
    label: "Dewpoint",
    prefix: "nwp/gfs/temperature",
    unit: "°F",
    dataRange: [-63.67, 116.33],
    decode: "R",
    band: "G",
    quantized254: true,
    palette: DEWPOINT_PALETTE,
    // 7 ticks, matching the temperature scale but capped at 110°F since
    // dewpoint realistically tops out well below the air-temperature max.
    legendLabels: ["-65°F", "-32°F", "0°F", "32°F", "60°F", "85°F", "110°F"],
  },

  temperature: {
    label: "Temperature",
    prefix: "nwp/gfs/temperature",
    unit: "°F",
    dataRange: [-63.67, 116.33],
    decode: "R",
    band: "R",
    quantized254: true,
    palette: TEMPERATURE_PALETTE,
    // 7 ticks across the -63.67..116.33°F data range, rounded to friendly
    // values (ends rounded slightly outward past the true data range).
    legendLabels: ["-65°F", "-32°F", "0°F", "32°F", "60°F", "85°F", "120°F"],
  },

  apparent_temperature: {
    label: "Apparent Temp",
    prefix: "nwp/gfs/temperature",
    unit: "°F",
    dataRange: [-63.67, 116.33],
    decode: "R",
    band: "B",
    quantized254: true,
    palette: TEMPERATURE_PALETTE,
    // 7 ticks across the -63.67..116.33°F data range, rounded to friendly
    // values (ends rounded slightly outward past the true data range).
    legendLabels: ["-65°F", "-32°F", "0°F", "32°F", "60°F", "85°F", "120°F"],
  },

  wind: {
    label: "Wind",
    // Updated GFS pipeline: U/V wind now lives in the wind_prate group
    // (R = u10, G = v10, B = precipitation rate — see precip_rate below).
    prefix: "nwp/gfs/wind_prate",
    // Displayed in mph. The underlying data is still wind speed in m/s
    // (U,V components decoded from R,G), but the legend and unit are shown in
    // mph since that's what users here expect. The palette stop positions are
    // physical (normalized over the 0..70 m/s data range) and unit-independent,
    // so only the displayed labels/unit change — decoding is unaffected.
    unit: "mph",
    // Data range is 0..70 m/s (== 0..156.59 mph): each component is encoded
    // over -70..+70 m/s per the updated spec, and the shader clamps the
    // normalized magnitude to 1.0, so byte-space 1.0 = 70 m/s. Do NOT change
    // these numbers independently of the palette: every stop position below
    // was computed as mph / 156.586 against this exact range. (The legacy
    // windlayer files used ±50 m/s; all positions were rescaled by 50/70 when
    // the range widened so the colors stay pinned to the same physical speeds.)
    dataRange: [0, 70],
    decode: "RG", // U,V components in R,G — shader computes magnitude
    quantized254: true,
    // Palette sampled directly from the reference wind map's legend bar (the
    // GRLevel-style wind2025 ramp, in mph), reproduced stop-for-stop. Each
    // legend mph anchor was converted to a normalized [0..1] position over
    // dataRange [0, 70] m/s using pos = mph / 156.586 (70 m/s = 156.586 mph),
    // honoring the bar's non-linear tick spacing (dense 7.5→33 mph, sparse
    // 40→105 mph). The ramp runs blue→indigo→violet→magenta→white plateau
    // (~33–41 mph) →orange→amber→yellow→green, then a deliberate hard cliff
    // from green to magenta at ~95 mph (two adjacent stops at 0.6047/0.6048),
    // a fade to deep purple at 105 mph, and the purple held flat from there
    // to the 156.6 mph top of the widened range.
    // Alpha curve (unchanged): 0–5 mph @ 70% opacity (lets the basemap breathe
    // through calm air), linear ramp 5→20 mph from 70% to 100%, fully opaque
    // above 20 mph. The basemap labels and coastlines render on top via
    // beforeId (see App.tsx) so they stay visible even where alpha hits 100%.
    palette: [
      [0.0000, [140, 140, 176, 178]],
      [0.0342, [140, 140, 176, 180]],
      [0.0394, [153, 152, 193, 184]],
      [0.0445, [163, 164, 212, 188]],
      [0.0514, [158, 177, 237, 194]],
      [0.0530, [137, 176, 244, 195]],
      [0.0545, [122, 175, 243, 196]],
      [0.0560, [103, 170, 240, 197]],
      [0.0591, [73, 167, 238, 200]],
      [0.0606, [55, 165, 239, 201]],
      [0.0621, [37, 162, 241, 202]],
      [0.0636, [36, 158, 219, 203]],
      [0.0674, [22, 153, 236, 207]],
      [0.0771, [32, 130, 241, 214]],
      [0.0843, [40, 106, 225, 220]],
      [0.0856, [41, 94, 243, 221]],
      [0.0881, [58, 70, 237, 223]],
      [0.0907, [66, 46, 240, 225]],
      [0.0919, [73, 30, 240, 226]],
      [0.0945, [81, 12, 227, 228]],
      [0.0996, [109, 12, 230, 232]],
      [0.1034, [125, 18, 242, 235]],
      [0.1073, [142, 26, 236, 239]],
      [0.1111, [160, 35, 236, 242]],
      [0.1131, [154, 50, 220, 243]],
      [0.1162, [182, 45, 240, 246]],
      [0.1201, [188, 60, 241, 249]],
      [0.1239, [204, 72, 239, 252]],
      [0.1296, [220, 82, 244, 255]],
      [0.1431, [228, 105, 228, 255]],
      [0.1533, [247, 122, 248, 255]],
      [0.1571, [236, 143, 244, 255]],
      [0.1654, [255, 152, 255, 255]],
      [0.1750, [245, 181, 241, 255]],
      [0.1839, [252, 197, 252, 255]],
      [0.1890, [249, 213, 248, 255]],
      [0.1993, [255, 239, 253, 255]],
      [0.2681, [255, 237, 227, 255]],
      [0.2798, [253, 222, 204, 255]],
      [0.2947, [255, 200, 175, 255]],
      [0.3080, [253, 184, 148, 255]],
      [0.3212, [255, 167, 121, 255]],
      [0.3345, [255, 150, 95, 255]],
      [0.3461, [252, 134, 72, 255]],
      [0.3594, [254, 117, 49, 255]],
      [0.3710, [252, 102, 25, 255]],
      [0.3826, [235, 92, 22, 255]],
      [0.3859, [255, 87, 0, 255]],
      [0.4039, [251, 109, 4, 255]],
      [0.4301, [254, 133, 7, 255]],
      [0.4529, [252, 160, 0, 255]],
      [0.4741, [255, 183, 0, 255]],
      [0.4954, [255, 206, 0, 255]],
      [0.5166, [255, 229, 0, 255]],
      [0.5265, [250, 244, 0, 255]],
      [0.5412, [249, 252, 25, 255]],
      [0.5479, [233, 255, 6, 255]],
      [0.5546, [205, 255, 25, 255]],
      [0.5579, [190, 255, 29, 255]],
      [0.5629, [174, 254, 42, 255]],
      [0.5663, [158, 254, 55, 255]],
      [0.5729, [133, 255, 65, 255]],
      [0.5796, [108, 255, 73, 255]],
      [0.5864, [80, 255, 90, 255]],
      [0.5914, [64, 253, 94, 255]],
      [0.5964, [47, 253, 106, 255]],
      [0.6014, [19, 255, 121, 255]],
      [0.6047, [6, 255, 132, 255]],
      [0.6048, [218, 24, 217, 255]],
      [0.6099, [244, 1, 241, 255]],
      [0.6186, [216, 0, 212, 255]],
      [0.6239, [190, 3, 196, 255]],
      [0.6309, [174, 0, 172, 255]],
      [0.6379, [148, 0, 151, 255]],
      [0.6449, [126, 0, 127, 255]],
      [0.6519, [102, 0, 102, 255]],
      [0.6589, [79, 0, 76, 255]],
      [0.6641, [58, 3, 57, 255]],
      [0.6706, [38, 0, 38, 255]],
      [0.7143, [38, 0, 38, 255]],
      [1.0000, [38, 0, 38, 255]],
    ],
    // 7 evenly-spaced ticks across 0..156.59 mph (== 0..70 m/s data range).
    legendLabels: ["0", "26", "52", "78", "104", "131", "157"],
  },

  // ─── Precipitation rate (new in wind_prate group) ─────────────────────────
  // Band 3 (B) of the wind_prate COG: precipitation rate, 0..5 mm/hr, encoded
  // 1..255 (byte 0 = NoData). Rides along in the same file the wind layer
  // already downloads, so enabling it costs no extra fetches. Byte 1 (0 mm/hr)
  // sits at palette position 0, which is fully transparent — dry areas let the
  // basemap show through; color ramps up through blue→green→yellow→red as the
  // rate climbs toward the 5 mm/hr cap.
  precip_rate: {
    label: "Precip Rate",
    prefix: "nwp/gfs/wind_prate",
    unit: "mm/hr",
    dataRange: [0, 5],
    decode: "R",
    band: "B",
    quantized254: true,
    palette: [
      [0.00, [120, 160, 255, 0]],   // 0.0 mm/hr — transparent (dry)
      [0.06, [120, 160, 255, 90]],  // ~0.3 — faint blue drizzle
      [0.16, [60, 130, 250, 170]],  // ~0.8 — light rain
      [0.30, [40, 190, 120, 220]],  // ~1.5 — green, moderate
      [0.50, [250, 220, 40, 240]],  // ~2.5 — yellow
      [0.70, [250, 140, 30, 250]],  // ~3.5 — orange
      [0.88, [230, 50, 40, 255]],   // ~4.4 — red, heavy
      [1.00, [160, 10, 60, 255]],   // 5.0 — dark red cap
    ],
    // 6 evenly-spaced ticks across 0..5 mm/hr.
    legendLabels: ["0", "1", "2", "3", "4", "5"],
  },

  // ─── Visibility/atmospheric triplet ──────────────────────────────────────
  // Source COG (prefix "nwp/gfs/r2_tcc_vis") packs 3 atmospheric
  // variables, encoded 1..255 with byte 0 = NoData (quantized254):
  //   Band 1 (R): Relative humidity 2m  (0..100 %)
  //   Band 2 (G): Total cloud cover     (0..100 %)
  //   Band 3 (B): Visibility            (0..25000 m)
  // Unlike the legacy vis_layer files (whose cloud/visibility bytes were
  // empirically inverted), the updated pipeline encodes all three bands with
  // the straight vmin→byte 1, vmax→byte 255 mapping.
  relative_humidity: {
    label: "Relative Humidity",
    prefix: "nwp/gfs/r2_tcc_vis",
    unit: "%",
    dataRange: [0, 100],
    decode: "R",
    band: "R",
    quantized254: true,
    // Blue gradient: pale at low RH (dry), deep navy at high RH (saturated).
    palette: HUMIDITY_PALETTE,
    // 7 evenly-spaced ticks across the 0..100% data range.
    legendLabels: ["0%", "17%", "33%", "50%", "67%", "83%", "100%"],
  },

  cloud_cover: {
    label: "Cloud Cover",
    prefix: "nwp/gfs/r2_tcc_vis",
    unit: "%",
    dataRange: [0, 100],
    decode: "R",
    band: "G",
    quantized254: true,
    // Updated pipeline: byte 0 is a true NoData sentinel (default zero-discard
    // stays ON), byte 1 = 0% coverage, byte 255 = 100% coverage — the straight
    // vmin→vmax mapping, NOT the inverted encoding of the legacy vis_layer
    // files. The palette below is the same gamma-shaped satellite-IR ramp as
    // before, mirrored to match: dark grey at 0% (clear), near-white at 100%
    // (overcast).
    //
    // GAMMA-SHAPED (not linear), same reasoning as the legacy version: a
    // straight ramp put mid-coverage (~30–60%) at a perceptually dark mid-grey
    // that read as "mostly clear" against the dark basemap. Brightness is held
    // HIGH across the cloudy half of the range and the falloff to dark is
    // pushed toward the clear (low-byte) end: the curve drops steeply over the
    // first ~30% of the range and stays light from ~40% coverage upward.
    //
    //   byte   1  (clear /   0%)   → dark grey    ( 50, 50, 50)
    //   byte  32  (~12% coverage)  → dark-mid     ( 90, 90, 90)
    //   byte  64  (~25% coverage)  → mid          (135,135,135)
    //   byte 103  (~40% coverage)  → light-mid    (175,175,175)  ← still clearly cloudy
    //   byte 141  (~55% coverage)  → light        (200,200,200)
    //   byte 192  (~75% coverage)  → bright       (225,225,225)
    //   byte 255  (overcast /100%) → near-white   (245,245,245)
    //
    // Fully opaque across the whole range (no transparent tail): genuine
    // no-data is the shader's sentinel job, not a palette position.
    palette: [
      [0.000, [50, 50, 50, 255]],    // 0%   — clear, dark grey
      [0.120, [90, 90, 90, 255]],    // ~12% — dark-mid
      [0.250, [135, 135, 135, 255]], // ~25% — mid
      [0.400, [175, 175, 175, 255]], // ~40% — light-mid, still cloudy
      [0.550, [200, 200, 200, 255]], // ~55% — light
      [0.750, [225, 225, 225, 255]], // ~75% — bright
      [1.000, [245, 245, 245, 255]], // 100% — overcast, near-white
    ],
    legendLabels: ["0%", "25%", "50%", "75%", "100%"],
  },

  visibility: {
    label: "Visibility",
    prefix: "nwp/gfs/r2_tcc_vis",
    unit: "m",
    dataRange: [0, 25000],
    decode: "R",
    band: "B",
    quantized254: true,
    // Updated pipeline: byte 0 is a true NoData sentinel (default zero-discard
    // stays ON), byte 1 = 0 m (heavy fog), byte 255 = 25 km (clear) — the
    // straight vmin→vmax mapping, NOT the inverted encoding of the legacy
    // GMGSI-era files. Same design intent as before: highlight LOW visibility
    // (fog, haze, dense smoke) and hide clear-sky areas, so the ramp is
    // mirrored to put the dark opaque end at the LOW bytes:
    //   byte   1 → dark grey, fully opaque (heavy fog — most prominent)
    //   byte ~50 → medium grey
    //   byte 128 → light grey, partially transparent
    //   byte 200 → near white, mostly transparent
    //   byte 255 → fully transparent (clear sky — basemap shows through)
    palette: [
      [0.00, [55,  55,  55,  235]], // dark grey, opaque (heavy fog)
      [0.20, [110, 110, 110, 215]],
      [0.40, [170, 170, 170, 180]],
      [0.60, [220, 220, 220, 130]],
      [0.80, [245, 245, 245, 60]],
      [1.00, [255, 255, 255, 0]],   // transparent (clear)
    ],
    legendLabels: ["0km", "6km", "12km", "18km", "25km"],
  },

  // EU radar bytes use the SAME precip-type + intensity encoding as MRMS
  // (0 = no precip, 1-80 rain, 89-165 sleet, 174-254 snow, 255 no-data), so
  // we configure it as a precip-type layer with the same palettes and data
  // range as MRMS. Byte 255 (no-data) is handled by the LUT's transparent
  // tail built in buildPrecipTypeLUT.
  //
  // Differences from MRMS:
  //   - downsample:3 to match MRMS. EU rasters are already smaller, so this is
  //     mostly for consistency across the two halves of the "radar" composite —
  //     but it also lightens the European loop's contribution to the shared
  //     memory budget, which matters because BOTH sub-layers cache their full
  //     loop simultaneously (see the byte-budget note in app.tsx). Was 2.
  //   - Default maxCacheSize and secondsPerFrame (no large-raster pressure,
  //     no extra-slow playback needed — cells move slower in EU frames)
  radar_eumetnet: {
    label: "Radar (Europe)",
    hidden: true, // exposed via composite "radar" entry
    prefix: "radar/obs/eumetnet/realtime",
    nowcastPrefix: "radar/obs/eumetnet/nowcast",
    unit: "dBZ",
    dataRange: [0, 100],
    decode: "R",
    downsample: 3,
    // Cache the whole loop (realtime + nowcast) so the European half of the
    // radar composite also caches fully on open and doesn't re-decode on zoom.
    maxCacheSize: 32,
    smooth: false,
    simplePrecipType: true,
    palette: [],    // not used — layer builds its LUT via precipTypePalettes
    precipTypePalettes: {
      rain: normalizeRamp([
        // EUMETNET variant: blue→yellow crossover raised to ~45 dBZ (vs ~35 for
        // MRMS). EU reflectivity reads a bit hotter for the same precip, so the
        // blue band is stretched to span 6..44 and the yellow band starts at
        // ~45; orange/red/magenta/purple are compressed into 45..100 above it.
        // Stop positions ARE dBZ values (intensity01 = dBZ/100, domain 0..100).
        //   6..44   cyan -> blue, bottoming at the DARKEST blue ~44 dBZ
        //   45..48  yellow band
        //   50..54  orange
        //   56..64  red (heavy)
        //   68..80  red -> magenta/pink
        //   86..94  pink -> purple/violet (extreme)
        //   97..100 pale violet/white (max)
        [6, 0, 210, 235, 200],   [14, 0, 170, 240, 255],
        [24, 0, 100, 225, 255],  [34, 0, 45, 165, 255],
        [44, 5, 20, 105, 255],   [45, 235, 225, 40, 255],
        [48, 245, 235, 30, 255], [50, 235, 140, 25, 255],
        [54, 235, 90, 22, 255],  [56, 228, 55, 20, 255],
        [60, 235, 30, 22, 255],  [64, 240, 25, 28, 255],
        [68, 235, 20, 95, 255],  [72, 225, 15, 150, 255],
        [80, 220, 40, 200, 255], [86, 200, 80, 225, 255],
        [94, 180, 120, 235, 255],[97, 220, 190, 245, 255],
        [100, 245, 235, 250, 255],
      ]),
      sleet: normalizeRamp([
        [0, 255, 180, 240, 200], [10, 255, 130, 230, 255],
        [20, 255, 100, 220, 255],[30, 255, 80, 210, 255],
        [40, 255, 60, 200, 255], [50, 255, 40, 195, 255],
        [60, 255, 20, 190, 255], [70, 250, 10, 185, 255],
        [80, 245, 0, 180, 255],  [90, 235, 0, 170, 255],
        [95, 220, 0, 155, 255],  [98, 200, 0, 130, 255],
        [100, 170, 0, 100, 255],
      ]),
      snow: normalizeRamp([
        [0, 220, 220, 230, 200], [10, 255, 255, 255, 255],
        [20, 250, 250, 255, 255],[30, 245, 245, 255, 255],
        [40, 240, 240, 255, 255],[50, 235, 235, 255, 255],
        [60, 230, 230, 255, 255],[70, 220, 220, 255, 255],
        [80, 210, 210, 255, 255],[90, 195, 195, 255, 255],
        [95, 170, 170, 255, 255],[98, 130, 130, 255, 255],
        [100, 80, 80, 255, 255],
      ]),
    },
    legendLabels: ["Rain", "Sleet", "Snow"],
  },

  // ─── GMGSI satellite triplet ─────────────────────────────────────────────
  // Source COG (prefix "satellite/gmgsi") packs three satellite imagery
  // products into the RGB bands. Bytes are already in 0..255 display range,
  // so dataRange is just [0, 255] and the palette is a simple grayscale.
  //   Band 1 (R): GMGSI Longwave imagery
  //   Band 2 (G): GMGSI Shortwave imagery
  //   Band 3 (B): GMGSI Water vapor imagery
  satellite_longwave: {
    label: "Satellite (Longwave)",
    prefix: "satellite/gmgsi",
    unit: "",
    dataRange: [0, 255],
    decode: "R",
    band: "R",
    smooth: true,
    palette: SATELLITE_GRAYSCALE,
    legendLabels: ["Dark", "", "", "", "Bright"],
  },

  satellite_shortwave: {
    label: "Satellite (Shortwave)",
    prefix: "satellite/gmgsi",
    unit: "",
    dataRange: [0, 255],
    decode: "R",
    band: "G",
    smooth: true,
    palette: SATELLITE_GRAYSCALE,
    legendLabels: ["Dark", "", "", "", "Bright"],
  },

  satellite_water_vapor: {
    label: "Satellite (Water Vapor)",
    prefix: "satellite/gmgsi",
    unit: "",
    dataRange: [0, 255],
    decode: "R",
    band: "B",
    smooth: true,
    // Colorized water-vapor ramp — brown → tan → light tan → blue → white.
    // Byte positions tuned against the GMGSI byte distribution so that the
    // bulk of the imagery (drier air, warm cloud tops) reads in warm earth
    // tones, with saturated blue reserved for genuinely moist deep-cloud
    // regions:
    //   byte   0 → deep brown        (driest / warmest)
    //   byte 115 → mid tan           (mid-range air, ~position 0.45)
    //   byte 180 → light tan         (transition into the cool/moist regime)
    //   byte 220 → pure blue         (cold moist tops)
    //   byte 250 → still blue        (holds through cold tops)
    //   byte 255 → white tip         (coldest deep-convective peaks only)
    // Modeled loosely on the AccuWeather reference where the desaturated
    // dry/middle dominates and saturated color is reserved for thick moisture.
    palette: [
      [0.000, [70,  35,  15,  235]],
      [0.120, [115, 65,  30,  240]],
      [0.280, [160, 105, 55,  245]],
      [0.450, [200, 155, 95,  248]],
      [0.600, [225, 195, 130, 250]],
      [0.706, [220, 215, 175, 248]],   // byte 180 — light tan / pale-blue transition
      [0.780, [150, 185, 215, 248]],
      [0.863, [40,  85,  205, 252]],   // byte 220 — pure blue
      [0.980, [40,  85,  205, 252]],   // hold blue through ~byte 250
      [1.000, [255, 255, 255, 255]],   // brief white tip on the coldest tops
    ],
    legendLabels: ["Dry", "", "", "", "Moist"],
  },

  // ─── Composite radar — MRMS (CONUS) + EUMETNET (Europe) in one toggle ───
  // The two sources cover disjoint regions, so they can be displayed at the
  // same time without overlap artifacts. This entry has no raster data of
  // its own — App.tsx expands composite[] into a set of independent raster
  // layers sharing a single scrubber and opacity slider.
  radar: {
    label: "Radar",
    prefix: "", // unused — composite has no direct data
    unit: "dBZ",
    dataRange: [0, 100],
    decode: "R",
    palette: [], // legend is drawn from sub-layers
    legendLabels: [],
    composite: ["radar_mrms", "radar_eumetnet"],
  },

  // ─── MRMS radar — precip-type encoded single-band layer ──────────────────
  // Byte value encodes BOTH category and intensity:
  //   0 → no precip (transparent), 1-80 rain, 81-88 transition, 89-165 sleet,
  //   166-173 transition, 174-254 snow, 255 no-data (transparent).
  // We build a 256-entry flat LUT with transition zones; the shader treats
  // this layer like any other continuous layer and samples the LUT directly.
  // Smoothing blurs the byte across category boundaries; transition zones in
  // the LUT make those intermediate values produce plausible colors.
  radar_mrms: {
    label: "Radar (MRMS)",
    hidden: true, // exposed via composite "radar" entry
    prefix: "radar/obs/mrms/realtime",
    nowcastPrefix: "radar/obs/mrms/nowcast",
    unit: "dBZ",
    dataRange: [0, 100],
    decode: "R",
    // downsample:3 (was 2) — cuts per-frame bytes ~2.25× vs downsample 2. This
    // is one of the two levers against the iOS WebKit 1.5 GB per-process memory
    // ceiling; the other is the hard byte budget on the frame cache in app.tsx.
    // maxCacheSize below is retained as a secondary, count-based bound.
    downsample: 3,
    // 32 = holds the ENTIRE loop simultaneously: the 20-frame realtime cap
    // plus a typical ~8-frame nowcast tail, with headroom, so the whole
    // animation caches on open and nothing evicts during scrubbing or replay.
    // NOTE: this count is only a secondary cap. The authoritative cap is the
    // MAX_CACHE_BYTES byte budget in app.tsx — a long nowcast tail can push the
    // dynamically-sized cache (kfs.length + 4) past 32 frames, so bytes, not
    // frame count, are what actually bound memory.
    maxCacheSize: 32,
    smooth: false,
    // simplePrecipType: A/B flag. Set to true to render via the minimal
    // "WSV3-style" path (SMOOTH==5 in the shader): sentinel-safe bilinear +
    // temporal blend + LUT lookup, no kernel/category-soft-membership/coverage
    // smoothstep. The complex SMOOTH==2 path stays as the default until we
    // validate that the simple path looks at least as good on real data.
    // Toggle to true here to test; flip back to remove or keep as default.
    simplePrecipType: true,
    // multiPassSmooth: deliberately off. The dual-FBO multipass pipeline
    // produces smoother gradients but inherently softens cell cores and
    // broadens cell edges (spatial Gaussian blur applied before palette
    // lookup). WSV3-style radar — and our own EU radar — get sharper, more
    // saturated output by skipping that pipeline entirely and letting the
    // single-pass 29-tap kernel run inline. The kernel-signature contour
    // boxes that motivated the multipass design only show up at extreme
    // zoom (>10) on stair-stepped source data, and at typical viewing
    // zooms the single-pass output looks better.
    //
    // The dual-FBO pipeline code is preserved in weatherRasterLayer.ts and
    // can be re-enabled by flipping this flag. It might still be the right
    // answer for some future layer with very dense, smoothly-varying source
    // data where halos aren't a concern.
    secondsPerFrame: 4.0,
    palette: [],        // not used — the layer builds its LUT via precipTypePalettes
    precipTypePalettes: {
      rain: normalizeRamp([
        // WSV3-legend-matched reflectivity scale. Stop positions ARE dBZ values
        // (intensity01 = dBZ/100, ramp domain 0..100), so they line up directly
        // with the reference legend. Progression per the legend:
        //   6..35   cyan -> blue, bottoming at the DARKEST blue ~35 dBZ
        //   36..40  yellow band
        //   42..45  orange
        //   46..54  red (heavy) — widened so red occupies the core band
        //   58..72  red -> magenta/pink
        //   80..90  pink -> purple/violet (extreme)
        //   96..100 pale violet/white (max)
        // Note this is a blue->yellow->red->magenta->purple scale, NOT the old
        // blue->yellow->orange->red one — it matches WSV3, which has only a
        // narrow yellow band and goes to magenta/purple at the top, not orange.
        // The single-step blue->yellow crossover at ~31 produces one muted
        // pixel (opposite hues); kept tight so it doesn't read as a band.
        [6, 0, 210, 235, 200],   [12, 0, 170, 240, 255],
        [20, 0, 100, 225, 255],  [28, 0, 45, 165, 255],
        [35, 5, 20, 105, 255],  [36, 235, 225, 40, 255],
        [39, 245, 235, 30, 255], [42, 235, 140, 25, 255],
        [46, 228, 55, 20, 255],  [50, 235, 30, 22, 255],
        [54, 240, 25, 28, 255],  [58, 235, 20, 95, 255],
        [64, 225, 15, 150, 255], [72, 220, 40, 200, 255],
        [80, 200, 80, 225, 255], [90, 180, 120, 235, 255],
        [96, 220, 190, 245, 255],[100, 245, 235, 250, 255],
      ]),
      sleet: normalizeRamp([
        [0, 255, 180, 240, 200], [10, 255, 130, 230, 255],
        [20, 255, 100, 220, 255],[30, 255, 80, 210, 255],
        [40, 255, 60, 200, 255], [50, 255, 40, 195, 255],
        [60, 255, 20, 190, 255], [70, 250, 10, 185, 255],
        [80, 245, 0, 180, 255],  [90, 235, 0, 170, 255],
        [95, 220, 0, 155, 255],  [98, 200, 0, 130, 255],
        [100, 170, 0, 100, 255],
      ]),
      snow: normalizeRamp([
        [0, 220, 220, 230, 200], [10, 255, 255, 255, 255],
        [20, 250, 250, 255, 255],[30, 245, 245, 255, 255],
        [40, 240, 240, 255, 255],[50, 235, 235, 255, 255],
        [60, 230, 230, 255, 255],[70, 220, 220, 255, 255],
        [80, 210, 210, 255, 255],[90, 195, 195, 255, 255],
        [95, 170, 170, 255, 255],[98, 130, 130, 255, 255],
        [100, 80, 80, 255, 255],
      ]),
    },
    legendLabels: ["Rain", "Sleet", "Snow"],
  },
};

/**
 * Build a 256x1 RGBA palette texture from sparse stops.
 * The shader samples this with the (interpolated) data value in [0..1].
 */
export function buildPaletteTexture(stops: PaletteStop[]): Uint8Array {
  const lut = new Uint8Array(256 * 4);
  if (stops.length === 0) return lut;
  for (let i = 0; i < 256; i++) {
    const t = i / 255;
    let lo = 0, hi = stops.length - 1;
    for (let j = 0; j < stops.length - 1; j++) {
      if (t >= stops[j][0] && t <= stops[j + 1][0]) { lo = j; hi = j + 1; break; }
    }
    const range = stops[hi][0] - stops[lo][0];
    const f = range > 0 ? (t - stops[lo][0]) / range : 0;
    const [rL, gL, bL, aL] = stops[lo][1];
    const [rH, gH, bH, aH] = stops[hi][1];
    lut[i * 4]     = Math.round(rL + (rH - rL) * f);
    lut[i * 4 + 1] = Math.round(gL + (gH - gL) * f);
    lut[i * 4 + 2] = Math.round(bL + (bH - bL) * f);
    lut[i * 4 + 3] = Math.round(aL + (aH - aL) * f);
  }
  return lut;
}

/**
 * Build a flat 256-entry LUT for precip-type encoded data (MRMS).
 *
 * Byte encoding (per MRMS ptype spec):
 *   0           → no precip (transparent)
 *   1..84       → rain    (palette sampled with intensity (byte-1)/83)
 *   85..169     → sleet   (palette sampled with intensity (byte-85)/84)
 *   170..254    → snow    (palette sampled with intensity (byte-170)/84)
 *   255         → no data (transparent)
 *
 * The ranges are adjacent (no gaps in the encoding), so there are no
 * "transition bytes" to fill. But pixel-level blurring (via texture filtering
 * and the in-shader Gaussian) can produce fractional values that round to a
 * category-boundary byte — and the abrupt hue change between rain's red
 * (byte 84) and sleet's pink (byte 85) is intentional per the spec: that IS
 * where rain ends and sleet begins.
 *
 * To soften the visual transition at those boundaries without introducing
 * "fake" colors, we optionally blend a small window around each boundary
 * using the palette colors themselves (rain end → sleet start, etc.).
 */
export function buildPrecipTypeLUT(palettes: {
  rain: PaletteStop[];
  sleet: PaletteStop[];
  snow: PaletteStop[];
}): Uint8Array {
  const lut = new Uint8Array(256 * 4);

  const rainLUT  = buildPaletteTexture(palettes.rain);
  const sleetLUT = buildPaletteTexture(palettes.sleet);
  const snowLUT  = buildPaletteTexture(palettes.snow);

  // Discrete-band sampler. Snaps the intensity to the nearest defined
  // palette stop and returns that stop's exact color, rather than the
  // smoothly-interpolated value between adjacent stops. This produces the
  // WSV3-style "distinct color bands" radar look: storms appear as blotches
  // of solid color (light blue, medium blue, green, yellow, orange, red)
  // rather than smooth radial gradients ("paint-balls").
  //
  // The interpolated LUTs from buildPaletteTexture are still built above
  // because they carry the alpha values we want, but we now sample the
  // RGB by stop-index instead. Alpha is taken from the interpolated LUT
  // so the low-intensity fade ramp still works smoothly.
  const sampleDiscrete = (
    palLUT: Uint8Array,
    stops: PaletteStop[],
    intensity01: number,
  ) => {
    const t = Math.max(0, Math.min(1, intensity01));
    // Find the nearest stop position
    let nearestIdx = 0;
    let nearestDist = Math.abs(stops[0][0] - t);
    for (let s = 1; s < stops.length; s++) {
      const d = Math.abs(stops[s][0] - t);
      if (d < nearestDist) { nearestDist = d; nearestIdx = s; }
    }
    const rgb = stops[nearestIdx][1];
    // Take alpha from the interpolated LUT (preserves the low-intensity
    // fade ramp from applyLowCutoff downstream).
    const idx = Math.round(t * 255);
    return [rgb[0], rgb[1], rgb[2], palLUT[idx * 4 + 3]];
  };

  // Kept for backward compatibility / non-discrete paths — unused below.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sample = (palLUT: Uint8Array, intensity01: number) => {
    const idx = Math.round(Math.max(0, Math.min(1, intensity01)) * 255);
    return [
      palLUT[idx * 4],
      palLUT[idx * 4 + 1],
      palLUT[idx * 4 + 2],
      palLUT[idx * 4 + 3],
    ];
  };

  const setLUT = (i: number, rgba: number[]) => {
    lut[i * 4]     = rgba[0];
    lut[i * 4 + 1] = rgba[1];
    lut[i * 4 + 2] = rgba[2];
    lut[i * 4 + 3] = rgba[3];
  };

  // Byte 0: transparent (no precip)
  setLUT(0, [0, 0, 0, 0]);

  // Low-intensity edge fade. Earlier versions hard-cut everything below the
  // first solid-color stop (CUT_INTENSITY = 0.15) to suppress flicker, but
  // that erased entire stratiform rain shields and the leading edge of
  // winter fronts. The flicker is actually a *temporal* problem (a pixel
  // crosses the noise threshold between frames), better solved by the
  // shader's category-aware cross-fade — so we pull the cutoff way down and
  // rely on a gentle alpha ramp for the lowest few dBZ.
  //
  //   CUT_INTENSITY = 0.04 ≈ first 3 dBZ above floor (true sub-detection
  //                          noise — leave hard-cut)
  //   FADE_WIDTH    = 0.18 ≈ next ~14 dBZ fades alpha 0 → full
  //
  // Net effect: drizzle and light flurries are visible, edge flicker is
  // suppressed by the alpha ramp + temporal cross-fade rather than wholesale
  // erasure of light precip.
  const CUT_INTENSITY = 0.04;
  const FADE_WIDTH = 0.18;

  // Low-intensity alpha shaping. `fadeWidth` is the intensity span over which
  // alpha ramps 0 → full, just above the hard sub-noise cut:
  //   • Rain (fadeWidth = FADE_WIDTH = 0.18): light returns ramp from
  //     transparent → opaque over the lowest ~14 dBZ. Rain's low-intensity
  //     color is a pale cyan, so fading it out at the edges reads cleanly.
  //   • Snow & Sleet (fadeWidth = SNOW_SLEET_FADE_WIDTH, matched to rain):
  //     previously these held FULL palette opacity (alpha 200) all the way
  //     down to the hard cut (fadeAlpha = false). The intent was to let blobs
  //     "end on their true color" rather than a fade — but because their low-
  //     intensity colors are bright (snow near-white, sleet light magenta),
  //     that produced a fully-opaque bright edge sitting directly on the dark
  //     basemap, which reads as a halo RING around isolated snow/sleet blobs
  //     (visible where a blob sits on black with no neighboring precip to
  //     blend into; rain didn't show it because rain masses are connected and
  //     the edge is interior). The shader's edge feather can't fix this — the
  //     LUT alpha underneath is a cliff, not a ramp.
  //
  //     The fix: give snow/sleet the SAME alpha ramp as rain instead of a
  //     cliff. The bright low-intensity entry now ramps from transparent →
  //     opaque over the lowest ~14 dBZ exactly like rain, so it never
  //     composites at full strength over black. Edges are softer than the
  //     old hold-full-alpha look, but the ring is gone entirely. (A shorter
  //     ramp would keep edges crisper at the cost of a faint residual ring;
  //     this matches rain for a fully consistent, ring-free edge.)
  // In all cases the very bottom (≤ CUT_INTENSITY) is still hard-cut to
  // transparent to drop true sub-detection noise.
  //
  // Snow/sleet ramp width — matched to rain's FADE_WIDTH (0.18) so all three
  // categories fade identically at the edge. Lower this toward ~0.05 if you
  // want crisper snow/sleet blob edges and can tolerate a faint ring.
  const SNOW_SLEET_FADE_WIDTH = FADE_WIDTH;

  const applyLowCutoff = (rgba: number[], intensity01: number, fadeWidth: number): number[] => {
    if (intensity01 <= CUT_INTENSITY) {
      return [rgba[0], rgba[1], rgba[2], 0];
    }
    const fadeEnd = CUT_INTENSITY + fadeWidth;
    if (intensity01 < fadeEnd) {
      const t = (intensity01 - CUT_INTENSITY) / fadeWidth;
      const smooth = t * t * (3 - 2 * t);
      return [rgba[0], rgba[1], rgba[2], Math.round(rgba[3] * smooth)];
    }
    return rgba;
  };

  // Rain: bytes 1..84 (intensity across 83 values) — wide alpha ramp.
  for (let i = 1; i <= 84; i++) {
    const intensity = (i - 1) / 83;
    setLUT(i, applyLowCutoff(sampleDiscrete(rainLUT, palettes.rain, intensity), intensity, FADE_WIDTH));
  }

  // Sleet: bytes 85..169 (intensity across 84 values) — short alpha ramp.
  for (let i = 85; i <= 169; i++) {
    const intensity = (i - 85) / 84;
    setLUT(i, applyLowCutoff(sampleDiscrete(sleetLUT, palettes.sleet, intensity), intensity, SNOW_SLEET_FADE_WIDTH));
  }

  // Snow: bytes 170..254 (intensity across 84 values) — short alpha ramp.
  for (let i = 170; i <= 254; i++) {
    const intensity = (i - 170) / 84;
    setLUT(i, applyLowCutoff(sampleDiscrete(snowLUT, palettes.snow, intensity), intensity, SNOW_SLEET_FADE_WIDTH));
  }

  // ── TRANSITION ZONES ──────────────────────────────────────────────────────
  //
  // Hardware bilinear (SMOOTH==5) produces fractional byte values at category
  // boundaries. Without a transition zone, byte 84.5 snaps to either byte 84
  // (dark red rain) or byte 85 (pink sleet) — a jarring color cliff. By
  // blending the LUT colors across a small window around each boundary, the
  // intermediate values get plausible in-between colors and the transition
  // looks smooth on screen.
  //
  // HALF_WIDTH controls how many bytes on each side of the boundary participate
  // in the blend. 3 bytes ≈ ~3.5% of a category's range — small enough that
  // the transition is visually narrow, large enough that hardware bilinear's
  // interpolated values all land inside it.
  const TRANSITION_HALF = 3;

  // Blend RGB toward the neighbor category, but DO NOT let the blend raise a
  // byte's alpha above the alpha it already had.
  //
  // Why: the low-intensity fade above deliberately drove the lowest sleet/snow
  // bytes (85.., 170..) to near-zero alpha so blob edges taper into the dark
  // basemap. The transition blend pulls its endpoint alpha from a byte
  // TRANSITION_HALF inside the previous category (an opaque, high-intensity
  // entry), so a naive RGBA blend re-opaques those edge bytes AND darkens
  // their color toward the neighbor's mid-tone. The result is a dark maroon
  // band (around sleet) and dark purple band (around snow) appearing OUTSIDE
  // the blob, where bilinear smear lands on exactly these bytes over black.
  // Clamping alpha to min(blended, original) keeps the smooth color transition
  // for genuinely-present mid-intensity boundary pixels while leaving the
  // faded-out edge bytes transparent.
  const blendRGBClampAlpha = (a: number[], b: number[], t: number, origAlpha: number): number[] => [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
    Math.min(Math.round(a[3] + (b[3] - a[3]) * t), origAlpha),
  ];

  const getLUT = (i: number): number[] => [
    lut[i * 4], lut[i * 4 + 1], lut[i * 4 + 2], lut[i * 4 + 3],
  ];

  // SEAM_HALF: how many bytes ON EACH SIDE of a boundary are the "seam" — the
  // bytes only ever produced when two real precip categories physically abut
  // (hardware bilinear interpolating across the boundary). These get FULL
  // opacity so two touching masses connect with no transparent gap, in the
  // byte-reading paths (SMOOTH==2 / SMOOTH==5).
  //
  // Bytes in the transition window but OUTSIDE the seam (further toward each
  // category's light end) are where an ISOLATED blob edge decays — those keep
  // the alpha clamp so they stay faded and don't bring back the outer ring.
  //
  // SEAM_HALF must be < TRANSITION_HALF so there's always at least one clamped
  // byte between the opaque seam and pure category color. 1 ≈ the single byte
  // either side of the exact boundary.
  const SEAM_HALF = 1;

  // Apply a transition zone at `boundary` (last byte of the lower category).
  // colorA = pure lower category, colorB = pure upper category. Inside the
  // seam window the blended color is written at full opacity; outside it the
  // alpha is clamped to the byte's pre-transition (possibly faded) alpha.
  //
  // The window spans [boundary-TRANSITION_HALF .. boundary+1+TRANSITION_HALF]
  // INCLUSIVE and is iterated by byte index directly. (The earlier version
  // looped over an offset j with a `(j>0?1:0)` skip, which never wrote
  // byte boundary+1 — the first upper-category byte — leaving the single most
  // important seam byte at whatever alpha the low-intensity fade gave it, i.e.
  // transparent. That skipped byte was the transparent gap between two
  // touching masses. Iterating indices directly fixes it.)
  const applyTransition = (boundary: number) => {
    const lo = boundary - TRANSITION_HALF;
    const hi = boundary + 1 + TRANSITION_HALF;
    const colorA = getLUT(lo);          // pure lower category
    const colorB = getLUT(hi);          // pure upper category
    for (let idx = lo; idx <= hi; idx++) {
      const t = (idx - lo) / (hi - lo); // 0 at lo (pure A) → 1 at hi (pure B)
      const blended = blendRGBClampAlpha(colorA, colorB, t, getLUT(idx)[3]);
      // The true boundary sits between byte `boundary` and `boundary+1`. Seam
      // bytes are within SEAM_HALF of that pair: idx in
      // [boundary-(SEAM_HALF-1) .. boundary+SEAM_HALF]. These get full opacity
      // so two touching masses connect; outer transition bytes keep their
      // clamped (faded) alpha so an isolated blob edge stays ring-free.
      const inSeam = idx >= boundary - (SEAM_HALF - 1) && idx <= boundary + SEAM_HALF;
      if (inSeam) {
        blended[3] = 255;
      }
      setLUT(idx, blended);
    }
  };

  // Boundary 1: rain (84) ↔ sleet (85)
  applyTransition(84);

  // Boundary 2: sleet (169) ↔ snow (170)
  applyTransition(169);

  // Byte 255: transparent (no data)
  setLUT(255, [0, 0, 0, 0]);

  return lut;
}